// Smart Sash Admin Panel - Lab Selection Component
// Dropdown to select a lab during RFID assignment and device registration flows

import { Spinner } from '@/components/common';
import { LabDTO } from '@/models';
import { labService } from '@/services/api/labService';
import React, { useEffect, useState } from 'react';

export interface LabSelectionResult {
  labId: string;
  institutionId: string;
  labName: string;
}

interface LabSelectionProps {
  onSelect: (result: LabSelectionResult) => void;
}

export const LabSelection: React.FC<LabSelectionProps> = ({ onSelect }) => {
  const [labs, setLabs] = useState<LabDTO[]>([]);
  const [selectedLabId, setSelectedLabId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLabs = async () => {
      try {
        // Public onboarding depends on the backend returning only labs that are valid
        // for the current registration session or authenticated user context.
        const response = await labService.getAll({ page: 1, pageSize: 10000 });
        setLabs(response.data);
      } catch {
        setError('Failed to load labs. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLabs();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const selected = labs.find((lab) => lab.id === selectedLabId);
    if (!selected) return;

    onSelect({
      labId: selected.id,
      institutionId: selected.institutionId,
      labName: selected.name ?? '',
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner />
        <p className="mt-3 text-muted">Loading labs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-circle me-2"></i>
        {error}
      </div>
    );
  }

  if (labs.length === 0) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        No labs available. Please contact your administrator.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <label htmlFor="labSelect" className="form-label">
          Select Lab
        </label>
        <select
          id="labSelect"
          className="form-select"
          value={selectedLabId}
          onChange={(e) => setSelectedLabId(e.target.value)}
          required
        >
          <option value="" disabled>
            Choose a lab...
          </option>
          {labs.map((lab) => (
            <option key={lab.id} value={lab.id}>
              {lab.name}
            </option>
          ))}
        </select>
      </div>

      <button type="submit" className="btn btn-primary w-100" disabled={!selectedLabId}>
        <i className="fas fa-arrow-right me-2"></i>
        Continue
      </button>
    </form>
  );
};

export default LabSelection;
